import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import Input from "components/input"
import { Item } from "components/Item/Item"
import { ItemContainer } from "components/ItemContainer/ItemContainer"
import request from "request"
import {
  carEvaluation,
  educations,
  employmentLevel,
  employmentLevels,
  employmentSpheres,
  familyStatuses,
  genders,
  loanType,
  productType,
  states,
  yesNoCoBorrower,
  yesNoOptions,
} from "utils/constants"
import { getRiskLabel } from "utils/getRiskLabel"
import { normalizeText } from "utils/normalizeText"
import { useClientContext } from ".."
import styles from "../application-group.module.scss"

export interface ScoringForm {
  id: string
  applicationId: string
  customerId: string
  dependents: string
  familyStatus: string
  age: string
  workExperience: string
  monthlyIncome: string
  gender: string
  education: string
  employmentLevel: string
  employmentSphere: string
  coBorrower: string
  collateral: string
  loanDuration: string
  productType: string
  cbj_black_list: string
  internal_black_list: string
  inspector_legal_cases: string
  loan_type: string
  property_value: string
  property_age_at_end: string
  property_outside_regulations: string
  car_evaluation_score: string
  credit_card_limit: string
  target_market_list: string
  length_of_service: string
  total_personal_expenses_monthly: string
  active_ahli_bank_monthly_payment: string
  selected_monthly_payments: string[]
}

let timeout: NodeJS.Timeout
const Scoring = () => {
  const { group, application, setGroup } = useClientContext()
  const { id, clientId } = useParams()

  const [form, setForm] = React.useState<ScoringForm>({
    id: "",
    applicationId: "",
    customerId: "",
    dependents: "",
    familyStatus: "",
    age: "",
    workExperience: "",
    monthlyIncome: "",
    gender: "",
    education: "",
    employmentLevel: "",
    employmentSphere: "",
    coBorrower: "",
    collateral: "",
    loanDuration: "",
    productType: "",
    cbj_black_list: "",
    internal_black_list: "",
    inspector_legal_cases: "",
    loan_type: "",
    property_value: "",
    property_age_at_end: "",
    property_outside_regulations: "",
    car_evaluation_score: "",
    credit_card_limit: "",
    target_market_list: "",
    length_of_service: "",
    total_personal_expenses_monthly: "",
    active_ahli_bank_monthly_payment: "",
    selected_monthly_payments: [],
  })

  const risk = application?.risk_log?.risks

  const [validate, setValidate] = useState({
    customerId: false,
    applicationId: false,
  })

  useEffect(() => {
    setForm((prev: any) => {
      const newForm = {
        id: String(application.id),
        applicationId: application.applicationId ?? "",
        customerId: application.customerId ?? "",
        dependents: String(application.scoring_log?.dependents ?? ""),
        familyStatus: application.scoring_log?.familyStatus ?? "",
        age:
          application.scoring_log?.age !== null
            ? String(application?.scoring_log?.age)
            : "",
        workExperience:
          application.scoring_log?.workExperience !== null
            ? String(application?.scoring_log?.workExperience)
            : "",
        monthlyIncome:
          application.scoring_log?.monthlyIncome !== null
            ? String(application?.scoring_log?.monthlyIncome)
            : "",
        active_ahli_bank_monthly_payment:
          application.scoring_log?.active_ahli_bank_monthly_payment !== null
            ? String(application?.scoring_log?.active_ahli_bank_monthly_payment)
            : "",
        productType:
          application.scoring_log?.productType !== null
            ? String(application?.scoring_log?.productType)
            : "",
        gender: application.scoring_log?.gender ?? "",
        education: application.scoring_log?.education ?? "",
        employmentLevel: application.scoring_log?.employmentLevel ?? "",
        employmentSphere: application.scoring_log?.employmentSphere ?? "",
        coBorrower: application.scoring_log?.coBorrower ?? "",
        collateral: application.scoring_log?.collateral ?? "",
        loanDuration: application.scoring_log?.loanDuration ?? "",
        cbj_black_list: application.scoring_log?.cbj_black_list ?? "",
        internal_black_list: application.scoring_log?.internal_black_list ?? "",
        inspector_legal_cases:
          application.scoring_log?.inspector_legal_cases ?? "",
        loan_type: application.scoring_log?.loan_type ?? "",
        property_value: application.scoring_log?.property_value ?? "",
        property_age_at_end: application.scoring_log?.property_age_at_end ?? "",
        property_outside_regulations:
          application.scoring_log?.property_outside_regulations ?? "",
        car_evaluation_score:
          application.scoring_log?.car_evaluation_score ?? "",
        credit_card_limit: application.scoring_log?.credit_card_limit ?? "",
        target_market_list: application.scoring_log?.target_market_list ?? "",
        length_of_service: application.scoring_log?.length_of_service ?? "",
        total_personal_expenses_monthly:
          application.scoring_log?.total_personal_expenses_monthly ?? "",
        selected_monthly_payments:
          application.scoring_log?.selected_monthly_payments ?? [],
      }

      return String(application.id) === prev.id ? prev : newForm
    })
  }, [application])

  const handleChangeForm = (
    key: keyof typeof form,
    value: string | string[],
  ) => {
    clearTimeout(timeout)
    const newForm = { ...form, [key]: value }
    setForm(newForm)

    timeout = setTimeout(() => {
      const scoringData: any = {
        branch: application.scoring_log?.branch ?? null,
      }

      if (newForm.dependents !== "")
        scoringData.dependents = Number(newForm.dependents)
      if (newForm.familyStatus) scoringData.familyStatus = newForm.familyStatus
      if (newForm.age !== "") scoringData.age = Number(newForm.age)
      if (newForm.workExperience !== "")
        scoringData.workExperience = Number(newForm.workExperience)
      if (newForm.monthlyIncome !== "")
        scoringData.monthlyIncome = Number(newForm.monthlyIncome)

      if (newForm.active_ahli_bank_monthly_payment !== "")
        scoringData.active_ahli_bank_monthly_payment = Number(
          newForm.active_ahli_bank_monthly_payment,
        )

      if (newForm.gender) scoringData.gender = newForm.gender
      if (newForm.education) scoringData.education = newForm.education
      if (newForm.employmentLevel)
        scoringData.employmentLevel = newForm.employmentLevel
      if (newForm.employmentSphere)
        scoringData.employmentSphere = newForm.employmentSphere
      if (newForm.coBorrower) scoringData.coBorrower = newForm.coBorrower
      if (newForm.collateral) scoringData.collateral = newForm.collateral
      if (newForm.loanDuration) scoringData.loanDuration = newForm.loanDuration
      if (newForm.productType) scoringData.productType = newForm.productType
      if (newForm.cbj_black_list)
        scoringData.cbj_black_list = newForm.cbj_black_list
      if (newForm.internal_black_list)
        scoringData.internal_black_list = newForm.internal_black_list
      if (newForm.inspector_legal_cases)
        scoringData.inspector_legal_cases = newForm.inspector_legal_cases
      if (newForm.loan_type) scoringData.loan_type = newForm.loan_type
      if (newForm.property_value)
        scoringData.property_value = newForm.property_value
      if (newForm.property_age_at_end)
        scoringData.property_age_at_end = newForm.property_age_at_end
      if (newForm.property_outside_regulations)
        scoringData.property_outside_regulations =
          newForm.property_outside_regulations
      if (newForm.car_evaluation_score)
        scoringData.car_evaluation_score = newForm.car_evaluation_score
      if (newForm.credit_card_limit)
        scoringData.credit_card_limit = newForm.credit_card_limit
      if (newForm.target_market_list)
        scoringData.target_market_list = newForm.target_market_list
      if (newForm.length_of_service)
        scoringData.length_of_service = newForm.length_of_service

      if (newForm.total_personal_expenses_monthly)
        scoringData.total_personal_expenses_monthly =
          newForm.total_personal_expenses_monthly

      scoringData.selected_monthly_payments = newForm.selected_monthly_payments

      setValidate((prev) => ({
        ...prev,
        applicationId:
          !/^[a-zA-Z0-9]+$/.test(newForm.applicationId) ||
          newForm.applicationId.length !== 12,
        customerId: newForm.customerId.length !== 7,
      }))

      const data = {
        ...(newForm.applicationId &&
          newForm.applicationId.length === 12 && {
            applicationId: newForm.applicationId,
          }),
        ...(newForm.customerId &&
          newForm.customerId.length === 7 && {
            customerId: newForm.customerId,
          }),
        interestRate: application.interestRate,
        loanAmount: application.loanAmount,
        product: application.product,
        scoring_data: scoringData,
      }

      request
        .put(`/applications/group/${id}/${clientId}`, data)
        .then(() => {
          request
            .get(`/applications/group/${id}`)
            .then((res) => res.data)
            .then((res) => {
              setGroup(res)
            })
        })
        .catch((error) => {
          console.error(
            `Error updating application: ${error.response?.data?.message || error.message}`,
          )
        })
    }, 1000)
  }

  if (group.scoring_approved) {
    return (
      <div className={styles.scoring_result}>
        <div className={styles.section}>
          <div className={styles.title}>Credit Data</div>
          <div className={styles.item}>
            <div className={styles.key}>Amount</div>
            <div className={styles.value}>
              {String(application.loanAmount)} JOD
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Duration</div>
            <div className={styles.value}>
              {String(application.scoring_log?.loanDuration)} months
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Interest Rate (Annual)</div>
            <div className={styles.value}>
              {String(application.interestRate)}%
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Branch</div>
            <div className={styles.value}>
              {String(application.scoring_log?.branch)}
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Product</div>
            <div className={styles.value}>{String(application.product)}</div>
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.title}>Client Data</div>
          <div className={styles.item}>
            <div className={styles.key}>Customer Id</div>
            <div className={styles.value}>
              {String(application.customerId) ?? ""}
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Application Id</div>
            <div className={styles.value}>
              {String(application.applicationId) ?? ""}
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Gender</div>
            <div className={styles.value}>
              {
                genders.find(
                  (gender) =>
                    gender.key === String(application.scoring_log?.gender),
                )?.value
              }
            </div>
          </div>

          <div className={styles.item}>
            <div className={styles.key}>Family Status</div>
            <div className={styles.value}>
              {
                familyStatuses.find(
                  (gender) =>
                    gender.key ===
                    String(application.scoring_log?.familyStatus),
                )?.value
              }
            </div>
          </div>

          <div className={styles.item}>
            <div className={styles.key}>Age</div>
            <div className={styles.value}>
              {String(application.scoring_log?.age) ?? ""}
            </div>
          </div>

          <div className={styles.item}>
            <div className={styles.key}>Dependents</div>
            <div className={styles.value}>
              {String(application.scoring_log?.dependents)}
            </div>
          </div>

          <div className={styles.item}>
            <div className={styles.key}>Product Type</div>
            <div className={styles.value}>
              {String(application.scoring_log?.productType)}
            </div>
          </div>

          <div className={styles.item}>
            <div className={styles.key}>Education</div>
            <div className={styles.value}>
              {
                educations.find(
                  (education) =>
                    education.key ===
                    String(application.scoring_log?.education),
                )?.value
              }
            </div>
          </div>

          <div className={styles.item}>
            <div className={styles.key}>Monthly Income</div>
            <div className={styles.value}>
              {String(application.scoring_log?.monthlyIncome)} JOD
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Work Experience</div>
            <div className={styles.value}>
              {String(application.scoring_log?.workExperience)} years
            </div>
          </div>

          <div className={styles.item}>
            <div className={styles.key}>Employment Level</div>
            <div className={styles.value}>
              {
                employmentLevels.find(
                  (gender) =>
                    gender.key ===
                    String(application.scoring_log?.employmentLevel),
                )?.value
              }
            </div>
          </div>

          <div className={styles.item}>
            <div className={styles.key}>Employment Sphere</div>
            <div className={styles.value}>
              {String(application.scoring_log?.employmentSphere)} years
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Co-Borrower</div>
            <div className={styles.value}>
              {application.scoring_log?.coBorrower === "yes" ? "Yes" : "No"}
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Collateral</div>
            <div className={styles.value}>
              {application.scoring_log?.collateral === "yes" ? "Yes" : "No"}
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Total personal expenses (monthly)</div>
            <div className={styles.value}>
              {application.scoring_log?.total_personal_expenses_monthly}
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Active Ahli Bank monthly payment</div>
            <div className={styles.value}>
              {String(
                application.scoring_log?.active_ahli_bank_monthly_payment,
              )}
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.key}>Requested loan’s monthly payment</div>
            <div className={styles.value}>
              {Number(
                application.scoring_log?.requested_loan_monthly_payment,
              ).toFixed(2)}
            </div>
          </div>
          {Object.keys(
            application?.scoring_log?.selected_monthly_payments ?? {},
          )?.length > 0 && (
            <>
              <div className={styles.item}>
                <div className={styles.key}>
                  Sum of selected monthly payments
                </div>
                <div className={styles.value}>
                  {String(application.scoring_log?.sum_of_selected_payments)}
                </div>
              </div>
              <div className={styles.item}>
                <div className={styles.key}>Selected monthly payments</div>
                <div className={styles.value}>
                  {application.scoring_log?.selected_monthly_payments
                    ? Object.entries(
                        application.scoring_log?.selected_monthly_payments,
                      )
                        .map(([key, value]) => {
                          let labelKey = key
                            .replace(/_/g, " ")
                            .replace(
                              /\b(loan|credit card)\b/i,
                              (match) =>
                                match.charAt(0).toUpperCase() + match.slice(1),
                            )

                          const match = key.match(/\d+$/)
                          if (match) {
                            labelKey = labelKey.replace(/\d+$/, `#${match[0]}`)
                          }

                          return `${labelKey} — ${value}`
                        })
                        .join("\n")
                    : "-"}
                </div>
              </div>
            </>
          )}

          <div className={styles.item}>
            <div className={styles.key}>Over Indebtedness Ratio</div>
            <div className={styles.value}>
              {Math.round(
                Number(application.scoring_log?.over_independence_ratio) * 100,
              )}
              %
            </div>
          </div>
        </div>

        <ItemContainer title={"Product data"}>
          <>
            {application.product === "Housing" && (
              <Item
                title="Loan type"
                value={application.scoring_log?.loan_type}
              />
            )}
            {(application.product === "Housing" ||
              application.product === "Land purchase" ||
              application.product === "Mortgage" ||
              application.product === "Auto") && (
              <Item
                title="Property value"
                value={application.scoring_log?.property_value}
              />
            )}
            {(application.product === "Housing" ||
              application.product === "Auto") && (
              <Item
                title="Property age at loan maturity"
                value={application.scoring_log?.property_age_at_end}
              />
            )}
            {(application.product === "Housing" ||
              application.product === "Land purchase" ||
              application.product === "Mortgage") && (
              <Item
                title="Property outside boundaries of regulation"
                value={application.scoring_log?.property_outside_regulations}
              />
            )}
            {application.product === "Auto" && (
              <Item
                title="Car evaluation score"
                value={application.scoring_log?.car_evaluation_score}
              />
            )}
            {application.product === "Credit Card" && (
              <Item
                title="Credit card limit"
                value={application.scoring_log?.credit_card_limit}
              />
            )}
            {application.product === "Personal" && (
              <>
                <Item
                  title="Target market list"
                  value={application.scoring_log?.target_market_list}
                />
                <Item
                  title="Length of service (in months)"
                  value={application.scoring_log?.length_of_service}
                />
                <Item
                  title="Against collateral"
                  value={application.scoring_log?.collateral ?? ""}
                />
              </>
            )}
            {(application.product === "Housing" ||
              application.product === "Land purchase" ||
              application.product === "Mortgage" ||
              application.product === "Auto") && (
              <Item
                title="LTV"
                value={`${(application.scoring_log?.ltv * 100).toFixed(2)}%`}
              />
            )}
            <Item
              title="DBR"
              value={`${(application.scoring_log?.dbr * 100).toFixed(2)} %`}
            />
          </>
        </ItemContainer>

        <ItemContainer title="Verification">
          <>
            <Item
              title={"CBJ Black list / Returned Cheques"}
              value={String(application.scoring_log?.cbj_black_list)}
            />
            <Item
              title={"Internal black list"}
              value={String(application.scoring_log?.internal_black_list)}
            />
            <Item
              title={"Inspector / Financial legal cases"}
              value={String(application.scoring_log?.inspector_legal_cases)}
            />
          </>
        </ItemContainer>

        <ItemContainer title="Risks">
          {risk &&
            Object.entries(risk)
              .sort(
                ([keyA], [keyB]) =>
                  (keyA === "high_risk_count" ? 1 : 0) -
                  (keyB === "high_risk_count" ? 1 : 0),
              )
              .map(([key, value]) => (
                <Item
                  key={key}
                  title={normalizeText(key)}
                  value={getRiskLabel(String(value))}
                />
              ))}
        </ItemContainer>
      </div>
    )
  }
  const multipleInput = [
    {
      title: "Selected monthly payments",
      id: "selected_monthly_payments",
      options: application.ocr_log?.installments
        ? Object.entries(application.ocr_log.installments).map(
            ([key, value]) => {
              let labelKey = key
                .replace(/_/g, " ")
                .replace(
                  /\b(loan|credit card)\b/i,
                  (match) => match.charAt(0).toUpperCase() + match.slice(1),
                )

              const match = key.match(/\d+$/)
              if (match) {
                labelKey = labelKey.replace(/\d+$/, `#${match[0]}`)
              }

              return {
                key,
                value,
                label: `${labelKey} — ${value}`,
              }
            },
          )
        : [],
      key: "selected_monthly_payments",
    },
  ]

  return (
    <div className={styles.scoring}>
      <div className={styles.section}>
        <div className={styles.title}>Customer data</div>
        <div>
          <Input
            title="Customer ID"
            value={form.customerId}
            type="string"
            maxLength={7}
            placeholder="1234567"
            onInput={(value) => handleChangeForm("customerId", value)}
          />
          {validate.customerId && (
            <p className={styles.red}>
              Customer ID must be exactly 7 digits long.
            </p>
          )}
        </div>

        <div>
          <Input
            title="Application ID"
            value={form.applicationId}
            type="string"
            maxLength={12}
            placeholder="123ABC"
            onInput={(value) => handleChangeForm("applicationId", value)}
          />

          {validate.applicationId && (
            <p className={styles.red}>
              Application ID must be exactly 12 alphanumeric characters.
            </p>
          )}
        </div>

        <Input
          title="Gender"
          value={form.gender}
          type="select"
          options={genders}
          onSelect={(option) => handleChangeForm("gender", option.key)}
        />

        <Input
          title="Family status"
          id="familyStatus"
          value={form.familyStatus}
          type="select"
          options={familyStatuses}
          hasFilterText
          onSelect={(option) => handleChangeForm("familyStatus", option.key)}
        />

        <Input
          title="Employment Sphere"
          id="employmentSphere"
          value={form.employmentSphere}
          options={employmentSpheres}
          type="select"
          hasFilterText
          onSelect={(option) =>
            handleChangeForm("employmentSphere", option.key)
          }
        />

        <Input
          title="Education"
          id="education"
          value={form.education}
          type="select"
          hasFilterText
          options={educations}
          onSelect={(option) => handleChangeForm("education", option.key)}
        />

        <Input
          id="collateral"
          title="Collateral"
          value={form.collateral}
          options={yesNoCoBorrower}
          type="select"
          onSelect={(option) => handleChangeForm("collateral", option.key)}
        />

        <Input
          id="coBorrower"
          title="Co-Borrower"
          value={form.coBorrower}
          options={yesNoCoBorrower}
          type="select"
          onSelect={(option) => handleChangeForm("coBorrower", option.key)}
        />
        <Input
          id="productType"
          title="Type of product"
          value={form.productType}
          type="select"
          hasFilterText
          isArabic
          options={productType}
          onSelect={(option) => handleChangeForm("productType", option.key)}
        />
        <Input
          id="employmentLevel"
          title="Employment Level"
          value={form.employmentLevel}
          options={employmentLevel}
          type="select"
          hasFilterText
          onSelect={(option) => handleChangeForm("employmentLevel", option.key)}
        />

        <Input
          title="Age"
          value={form.age}
          step={1}
          min={18}
          max={80}
          placeholder="min: 18 max: 80"
          type="integer"
          onInput={(value) => handleChangeForm("age", value)}
        />

        <Input
          title="Monthly Income"
          value={form.monthlyIncome}
          step={1}
          max={30000}
          placeholder="min: 0 max: 30000"
          type="float"
          onInput={(value) => handleChangeForm("monthlyIncome", value)}
        />

        <Input
          title="Work Experience"
          value={form.workExperience}
          step={1}
          max={50}
          placeholder="min: 0 max: 50"
          type="integer"
          onInput={(value) => handleChangeForm("workExperience", value)}
        />

        <Input
          title="Dependents"
          value={form.dependents}
          step={1}
          max={40}
          placeholder="min: 0 max: 40"
          type="integer"
          onInput={(value) => handleChangeForm("dependents", value)}
        />
        <Input
          title="Total personal expenses (monthly)"
          value={form.total_personal_expenses_monthly}
          type="integer"
          step={1}
          placeholder="min: 0"
          onInput={(value) =>
            handleChangeForm("total_personal_expenses_monthly", value)
          }
        />
        <Input
          title="Active Ahli Bank monthly payment"
          value={form.active_ahli_bank_monthly_payment}
          type="integer"
          step={1}
          max={1000000}
          placeholder="min: 0 max: 1000000"
          onInput={(value) =>
            handleChangeForm("active_ahli_bank_monthly_payment", value)
          }
        />
        {multipleInput.map(({ title, id, options, key }) => (
          <Input
            key={id}
            hasFilterText
            title={title}
            id={id}
            multiple
            value={
              Array.isArray(form[key as keyof ScoringForm])
                ? form[key as keyof ScoringForm]
                : []
            }
            type="select"
            options={options.map(({ key, label }) => ({ key, value: label }))}
            onMultiSelect={(selectedKeys: string[]) =>
              handleChangeForm(key as keyof ScoringForm, selectedKeys)
            }
            placeholder="Selected monthly pyments of financial institutions from CRIF"
            disabled={!application.ocr_log}
          />
        ))}

        <div className={styles.title}>Product data</div>
        {application.product === "Housing" && (
          <>
            <Input
              id="loan_type"
              title="Loan Type"
              value={form.loan_type}
              options={loanType}
              type="select"
              onSelect={(option) => handleChangeForm("loan_type", option.key)}
            />
            <Input
              id="property_value"
              title="Property Value"
              value={form.property_value}
              type="integer"
              min={1}
              max={100000000}
              placeholder="min: 1 max: 100000000"
              onInput={(value) => handleChangeForm("property_value", value)}
            />
            <Input
              title="Property age at loan maturity"
              value={form.property_age_at_end}
              max={100}
              type="integer"
              placeholder="min: 0 max: 100"
              onInput={(value) =>
                handleChangeForm("property_age_at_end", value)
              }
            />
            <Input
              id="property_outside_regulations"
              title="Property outside boundaries of regulation"
              value={form.property_outside_regulations}
              options={yesNoOptions}
              type="select"
              onSelect={(option) =>
                handleChangeForm("property_outside_regulations", option.key)
              }
            />
          </>
        )}

        {(application.product === "Land purchase" ||
          application.product === "Mortgage") && (
          <>
            <Input
              title="Property Value"
              value={form.property_value}
              type="integer"
              min={1}
              max={100000000}
              placeholder="min: 1 max: 100000000"
              onInput={(value) => handleChangeForm("property_value", value)}
            />

            <Input
              id="property_outside_regulations"
              title="Property outside boundaries of regulation"
              value={form.property_outside_regulations}
              options={yesNoOptions}
              type="select"
              onSelect={(option) =>
                handleChangeForm("property_outside_regulations", option.key)
              }
            />
          </>
        )}

        {application.product === "Auto" && (
          <>
            <Input
              title="Property Value"
              value={form.property_value}
              type="integer"
              min={1}
              max={100000000}
              placeholder="min: 1 max: 100000000"
              onInput={(value) => handleChangeForm("property_value", value)}
            />
            <Input
              title="Property age at loan maturity"
              value={form.property_age_at_end}
              max={100}
              placeholder="min: 0 max: 100"
              type="integer"
              onInput={(value) =>
                handleChangeForm("property_age_at_end", value)
              }
            />
            <Input
              id="car_evaluation_score"
              title="Car Evaluation Score"
              value={form.car_evaluation_score}
              type="select"
              options={carEvaluation}
              onSelect={(option) =>
                handleChangeForm("car_evaluation_score", option.key)
              }
            />
          </>
        )}

        {application.product === "Credit Card" && (
          <Input
            title="Credit Card Limit"
            value={form.credit_card_limit}
            type="integer"
            max={100000000}
            placeholder="min: 0 max: 100000000"
            onInput={(value) => handleChangeForm("credit_card_limit", value)}
          />
        )}

        {application.product === "Personal" && (
          <>
            <Input
              id="target_market_list"
              title="Target Market List"
              value={form.target_market_list}
              options={yesNoOptions}
              type="select"
              onSelect={(option) =>
                handleChangeForm("target_market_list", option.key)
              }
            />
            <Input
              title="Length of Service"
              value={form.length_of_service}
              type="integer"
              max={100000000}
              placeholder="min: 0 max: 100000000"
              onInput={(value) => handleChangeForm("length_of_service", value)}
            />
          </>
        )}

        <div className={styles.title}>Verification</div>
        <Input
          id="cbj_black_list"
          title="CBJ Black list / Returned Cheques"
          value={form.cbj_black_list}
          options={states}
          type="select"
          onSelect={(option) => handleChangeForm("cbj_black_list", option.key)}
        />
        <Input
          id="internal_black_list"
          title="Internal Black list"
          value={form.internal_black_list}
          options={states}
          type="select"
          onSelect={(option) =>
            handleChangeForm("internal_black_list", option.key)
          }
        />
        <Input
          id="inspector_legal_cases"
          title="Inspector / Financial legal cases"
          value={form.inspector_legal_cases}
          options={states}
          type="select"
          onSelect={(option) =>
            handleChangeForm("inspector_legal_cases", option.key)
          }
        />
      </div>
    </div>
  )
}

export default Scoring
